import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  ActionsBanner,
  Button,
  ColumnsButton,
  ColumnsDrawer,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  IconLink,
  ListView,
  Page,
  SearchInput,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import DeactivateMemberConfirmation from './DeactivateMemberConfirmation';
import MemberAssociationsModal from './MemberAssociationsModal';
import { FilterButton, FiltersDrawer, MemberFiltersBar, MemberFiltersGroup } from '~/components/filters';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import {
  useDocumentTitle,
  useFeatures,
  useIsMounted,
  useMemberFilters,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import { BulkEditMember, BulkSendInvitation } from './bulk';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, dateFormats, mimeTypes } from '~/utils';
import DeleteMemberDialog from './DeleteMemberDialog';
import MemberInviteForm from './MemberInviteForm';
import MembersGrid from './MembersGrid';
import MembersList from './MembersList';

function MembersPage() {
  useDocumentTitle('Members');

  const { workspace } = useWorkspace();

  const api = useApi();

  const searchParamsConfig = useSearchParamsConfig();
  const memberFilters = useMemberFilters(() => ({
    memberStatusId: { ...searchParamsConfig.memberStatusId, default: 'active' },
  }));

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      q: '',
      memberSecurityRoles: [],
      page: 0,
      sort: new QuerySort('member.name', 'asc'),
      size: 50,
      view: 'grid',
      ...memberFilters.filters,
    },
    action: 'load',
    saved: false,
  });

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const done = () => {
    setQuery((state) => ({
      ...state,
      saved: false,
    }));
  };

  const add = () => {
    setQuery((state) => ({
      ...state,
      saved: true,
    }));

    setTimeout(() => {
      done();
    }, 1000);
  };

  const { isReady: isDataReady, data, params, searchParamsStatus, action } = query;

  const selection = useRowSelection({ rows: data?.results });

  const [isSlackInfoLoading, setIsSlackInfoLoading] = useState(true);
  const [isSlackConnected, setIsSlackConnected] = useState(false);
  const toast = useToast();
  const confirmation = useConfirmation();
  const isMounted = useIsMounted();

  // Filter persistency
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        memberSecurityRoles: searchParamsConfig.memberSecurityRoles,
        view: { default: 'grid', valid: ['grid', 'list'] },
        sort: { default: new QuerySort('member.name', 'asc'), ...searchParamsConfig.sort },
        ...memberFilters.searchParamsConfig,
      }),
      [searchParamsConfig, memberFilters],
    ),
    sessionKey: 'members_list',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const history = useHistory();
  const location = useLocation();
  const { url, path } = useRouteMatch();

  const isReady = useMemo(() => isDataReady && !isSlackInfoLoading, [isDataReady, isSlackInfoLoading]);

  const urlSearchParams = useMemo(() => {
    return {
      q: params.q,
      memberSecurityRoleId: params.memberSecurityRoles?.map((v) => v.id),
      sort: params.sort,
      ...memberFilters.mapUrlSearchParams(params),
    };
  }, [params, memberFilters]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .members()
        .list({ ...urlSearchParams, page: params.page, size: 50 });

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        isReady: true,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
    }
  }, [workspace.id, urlSearchParams, api, params, isMounted]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  useEffect(() => {
    (async () => {
      setIsSlackInfoLoading(true);

      const { data: isConnected } = await api.www.workspaces(workspace.id).integrations().slack.getConnected();

      setIsSlackInfoLoading(false);
      setIsSlackConnected(isConnected);
    })();
  }, [api, workspace.id, isMounted]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  const handleFilter = (values) => {
    if (values !== params) {
      setQuery((state) => ({ ...state, status: 'filtering' }));
      selection.clear();
    }

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  async function handleActiveStatusChange(member, isActive) {
    if (!isActive) {
      const confirm = await confirmation.prompt((resolve) => (
        <DeactivateMemberConfirmation memberId={member.id} workspaceId={workspace.id} resolve={resolve} />
      ));
      if (!confirm) return;
    }

    let activeEndDate = null;
    if (!isActive) {
      if (member.activeStartDate) {
        activeEndDate = moment.max(moment(member.activeStartDate), moment()).format(dateFormats.isoDate);
      } else {
        activeEndDate = moment().format(dateFormats.isoDate);
      }
    }

    try {
      const { data } = await api.www.workspaces(workspace.id).members(member.id).setActiveStatus({
        isActive,
        activeStartDate: member.activeStartDate,
        activeEndDate,
      });
      updateItem(data);
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleViewAssociations(member) {
    await confirmation.prompt((resolve) => (
      <MemberAssociationsModal memberId={member.id} workspaceId={workspace.id} resolve={resolve} />
    ));
  }

  async function handleRemove(member) {
    confirmation.prompt((resolve) => (
      <DeleteMemberDialog
        onCheckDependencies={async (workspace) => (await workspace.members(member.id).getAssociationTypes()).data}
        member={member}
        onClose={resolve}
        onDelete={async () => {
          fetchData();
          resolve(true);
        }}
      />
    ));
  }

  async function handleInviteSuccess(member) {
    add();
    history.push(`${url}/details/${member.id}/general`);
  }

  async function handleSendInvitation(member) {
    try {
      await api.www.workspaces(workspace.id).members(member.id).sendInvitation();
      const { data } = await api.www.workspaces(workspace.id).members().get({ ids: member.id });
      updateItem(data[0]);
      toast.success(`Successfully sent the invitation to join ${workspace.name} to ${member.name}.`);
    } catch (error) {
      toast.error(error?.message || `An error has occurred sending the invitation to ${member.name}.`);
    }
  }

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
    selection.clear();
  };

  function handleMemberClick(member) {
    history.push(`${url}/details/${member.id}`);
  }

  const handleViewChange = (view) => {
    const querySort = new QuerySort('member.name', 'asc');
    setParams({ ...params, sort: querySort, view });
    searchParams.set({ sort: null, view });
    selection.clear();
  };

  const handleBulkEdit = async () => {
    await confirmation.prompt((resolve) => (
      <BulkEditMember
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSendInvitation = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSendInvitation
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .members()
          .export(
            { ...urlSearchParams, size: null },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  const [dialog, setDialog] = useState(null);
  const features = useFeatures();

  const columns = useViewColumns({
    storageKey: 'members_list_columns',
    columns: _.compact([
      { key: 'name', name: 'Name', disabled: true },
      { key: 'type', name: 'Type', default: true },
      { key: 'billability', name: 'Billability', default: true },
      { key: 'securityRole', name: 'Security Role', default: true },
      { key: 'status', name: 'Status', default: true },
      { key: 'manager', name: 'Manager', default: true },
      { key: 'laborCostType', name: 'Labor Cost Type', default: false },
      { key: 'holidaySchedule', name: 'Holiday Schedule', default: false },
      features.businessUnits && { key: 'businessUnit', name: 'Business Unit', default: false },
      features.practices && { key: 'practice', name: 'Practice', default: false },
      features.disciplines && { key: 'discipline', name: 'Discipline', default: false },
      { key: 'location', name: 'Location', default: false },
      { key: 'internalId', name: 'Internal ID', default: false },
      { key: 'lastActive', name: 'Last Active', default: true },
    ]),
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  if (!isReady || !data) return <PageLoader />;
  return (
    <Page>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Settings</Page.Eyebrow>
          <Page.Title>Members</Page.Title>
        </Page.Info>

        <ViewNav
          value={params.view}
          onChange={(view) => {
            handleViewChange(view);
          }}
        />

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(`members.csv`, mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(`members.xlsx`, mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <ColumnsButton
            disabled={params.view === 'grid'}
            tooltip={params.view === 'grid' ? 'Switch to List View to manage columns' : 'Columns'}
            onClick={handleShowColumnsDrawer}
          />

          <FilterButton isOutline onClick={showFilters} />

          <IconLink
            tooltip="New Member"
            icon="plus"
            to={{ pathname: `${url}/new`, search: location.search }}
            data-testid="members_add_button"
          />
        </Page.Actions>
      </Page.Header>

      <>
        <Page.Filters>
          <FiltersBar>
            <SearchInput
              value={params.q}
              placeholder="Search"
              onChange={({ target: { value } }) => handleFilter({ q: value })}
            />

            <MemberFiltersBar
              filters={params}
              sticky={[
                'memberSecurityRoles',
                'memberManagers',
                'memberEmploymentTypes',
                'memberBillableTypeId',
                'memberInvitationStatuses',
                'memberStatusId',
                'memberSkills',
                'memberTags',
                'memberJobTitles',
                'memberBusinessUnits',
                'memberPractices',
              ]}
              onChange={handleFilter}
            />
          </FiltersBar>
        </Page.Filters>
        <Filters values={params} isOpen={filtersVisible} onApply={handleFilter} onClose={hideFilters} />
      </>

      {params.view === 'grid' && (
        <>
          <Page.Section>
            <MembersGrid
              data={data}
              action={action}
              isSlackConnected={isSlackConnected}
              onLoadMore={loadMore}
              onCardClick={handleMemberClick}
              onSendInvitation={handleSendInvitation}
              onActiveStatusChange={handleActiveStatusChange}
              onRemove={handleRemove}
            />

            <ListView.Status total={data.total} label="Member" isLoading={!!action} />
          </Page.Section>
        </>
      )}

      {params.view === 'list' && (
        <MembersList
          data={data}
          action={action}
          query={params}
          isSlackConnected={isSlackConnected}
          columns={columns}
          selection={selection}
          onLoadMore={loadMore}
          onRowClick={handleMemberClick}
          onSendInvitation={handleSendInvitation}
          onViewAssociations={handleViewAssociations}
          onActiveStatusChange={handleActiveStatusChange}
          onRemove={handleRemove}
          onSort={handleSort}
        />
      )}

      {params.view === 'list' && selection.any && (
        <ActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <>
                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkEdit();
                    setIsOpen(false);
                  }}>
                  Edit
                </BulkActionMenu.Item>

                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkSendInvitation();
                    setIsOpen(false);
                  }}>
                  Send Invitation
                </BulkActionMenu.Item>
              </>
            )}
          </BulkActionMenu>
        </ActionsBanner>
      )}

      <Route path={`${path}/new`}>
        <MemberInviteForm
          onClose={() => history.push({ pathname: url, search: location.search })}
          onInvited={handleInviteSuccess}
        />
      </Route>

      {dialog}
    </Page>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <MemberFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}

const Styled = {
  Nav: styled.div`
    align-self: center;
    display: flex;
    align-items: center;
    border-radius: 0.3125rem;
    border-color: ${colors.white};
    overflow: hidden;
    background: ${colors.white};
    margin-left: auto;
  `,

  Button: styled(Button)`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 1px;
    min-width: 5rem;
    height: 2rem;
    padding: 0 1rem;
    color: ${colors.grey40};
    background-color: ${colors.grey5};
    font-size: 0.75rem;
    font-weight: ${weights.black};
    letter-spacing: 0.0625rem;
    text-transform: uppercase;
    border-radius: 0;

    &:first-child {
      margin-left: 0;
    }

    &:hover {
      color: ${colors.grey40};
      background-color: ${colors.grey10};
    }

    ${({ $active }) =>
      $active &&
      css`
        color: ${colors.white};
        background-color: ${colors.primary};

        &:hover {
          color: ${colors.white};
          background-color: ${colors.accent};
        }
      `}
  `,
};

function ViewNav({ value, onChange }) {
  return (
    <Styled.Nav>
      <Styled.Button onClick={() => onChange('grid')} $active={value === 'grid'} data-testId="grid-button">
        Grid
      </Styled.Button>
      <Styled.Button onClick={() => onChange('list')} $active={value === 'list'} data-testId="list-button">
        List
      </Styled.Button>
    </Styled.Nav>
  );
}

export default MembersPage;
